import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  TextField
} from '@mui/material';

import { mapService, SavedMap } from '../../../services/api/mapService';
import ShareAccessDialog from '../../sharing/ShareAccessDialog';
import load_kml, { convertLegacyToCurrentLayers } from '../../../services/kml/legacyKmlParser';
import { AppContext } from '../../../context/context';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import { logEvent } from '../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors } from '../../../theme/opsTokens';

interface MapGalleryModalProps {
  open: boolean;
  onClose: () => void;
  onMapLoaded?: (mapId: string) => void;
  hasUnsavedChanges?: boolean;
  currentMapName?: string;
  onMapNameChange?: (name: string) => void;
  onSaveMap?: (mapName?: string) => Promise<boolean>;
}

export default function MapGalleryModal({
  open,
  onClose,
  onMapLoaded,
  hasUnsavedChanges = false,
  currentMapName = '',
  onMapNameChange,
  onSaveMap,
}: MapGalleryModalProps) {
  const { t } = useTranslation('mapDialogs');
  const context = useContext(AppContext);
  const fixedRoutesContext = useFixedRoutes();

  if (!context) {
    throw new Error("MapGalleryModal must be used within the context provider");
  }

  const { global, mapSettings } = context;
  const currentUserEmail = context.userData?.userDetails?.email;
  const { setLayers, setFleets } = global;
  const { setMapName, setMapCentre } = mapSettings;

  const [maps, setMaps] = useState<SavedMap[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMapId, setLoadingMapId] = useState<string | null>(null);
  const [shareMap, setShareMap] = useState<SavedMap | null>(null);
  const [mapsFilterTab, setMapsFilterTab] = useState<'all' | 'owned' | 'shared'>('all');
  const [brokenThumbnails, setBrokenThumbnails] = useState<Set<string>>(() => new Set());
  const [pendingLoadMap, setPendingLoadMap] = useState<SavedMap | null>(null);
  const [confirmUnsavedOpen, setConfirmUnsavedOpen] = useState(false);
  const [draftMapName, setDraftMapName] = useState(currentMapName);
  const [saveAndContinueLoading, setSaveAndContinueLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const unsavedNameInputRef = useRef<HTMLInputElement | null>(null);
  const wasOpenRef = useRef(false);
  const [mapDetails, setMapDetails] = useState<Map<string, {
    layers: number;
    shapes: number;
    fixedRoutes: number;
    stops: number;
    fleets: number;
  }>>(new Map());
  const fetchedDetailsRef = useRef<Set<string>>(new Set());

  // Load maps when modal opens
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    if (open) {
      loadMaps();
      // Clear cached details when modal opens to force refresh
      setMapDetails(new Map());
      fetchedDetailsRef.current.clear();
      setMapsFilterTab('all');
      setBrokenThumbnails(new Set());
      setPendingLoadMap(null);
      setConfirmUnsavedOpen(false);
      setSaveAndContinueLoading(false);
      setConfirmError(null);
      if (justOpened) {
        setDraftMapName((currentMapName || '').trim());
      }
    }
    wasOpenRef.current = open;
  }, [open, currentMapName]);

  useEffect(() => {
    if (!confirmUnsavedOpen) return;
    const focusTimer = window.setTimeout(() => {
      unsavedNameInputRef.current?.focus();
      unsavedNameInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [confirmUnsavedOpen]);

  // Fetch details for each map
  const fetchMapDetails = useCallback(async (mapId: string) => {
    try {
      const mapData = await mapService.loadMap(mapId);
      
      // Log a sample of the KML to debug
      console.log(`🔍 KML sample for map ${mapId}:`, mapData.file_data.substring(0, 500));
      
      const { layers: legacyLayers, fleets, fixedRoutes } = load_kml(mapData.file_data);
      const currentLayers = convertLegacyToCurrentLayers(legacyLayers);
      
      const details = {
        layers: currentLayers.length,
        shapes: currentLayers.reduce((total, layer) => total + layer.shapes.length, 0),
        fixedRoutes: fixedRoutes?.routes?.length || 0,
        stops: fixedRoutes?.stops?.length || 0,
        fleets: fleets?.length || 0,
      };
      
      console.log(`📊 Map ${mapId} details:`, details);
      
      setMapDetails(prev => new Map(prev).set(mapId, details));
      fetchedDetailsRef.current.add(mapId);
    } catch (err) {
      console.warn(`Failed to fetch details for map ${mapId}:`, err);
    }
  }, []);

  // Fetch details for all maps when maps are loaded
  useEffect(() => {
    if (maps.length > 0) {
      maps.forEach(map => {
        // Only fetch if we haven't already fetched details for this map
        if (!fetchedDetailsRef.current.has(map.zone_id)) {
          fetchMapDetails(map.zone_id);
        }
      });
    }
  }, [maps, fetchMapDetails]);

  const loadMaps = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await mapService.getUserMaps(0, 50); // Load up to 50 maps
      setMaps(response.maps);
    } catch (err) {
      console.error('Error loading maps:', err);
      const errorMessage = err instanceof Error ? err.message : t('mapGallery.errorLoadMaps');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const ownedMaps = maps.filter((m) => m.role === 'Owner');
  const sharedMaps = maps.filter((m) => m.role && m.role !== 'Owner');
  const filteredMaps =
    mapsFilterTab === 'owned' ? ownedMaps : mapsFilterTab === 'shared' ? sharedMaps : maps;

  const handleLoadMap = async (map: SavedMap) => {
    setLoadingMapId(map.zone_id);
    const startTime = Date.now();
    
    // Track load initiation
    logEvent({
      category: 'Map',
      action: 'LoadInitiated',
      label: 'Map Load Started',
      mapId: map.zone_id,
      mapName: map.zone_name,
      loadSource: 'gallery'
    });
    
    try {
      console.log('🗂️ Loading map:', map.zone_name);
      
      // Load map data from API
      const mapData = await mapService.loadMap(map.zone_id);
      
      // Parse KML data into layers, fleets, and fixed routes
      const { mapName, layers: legacyLayers, fleets, fixedRoutes } = load_kml(mapData.file_data);
      
      console.log('📋 Parsed KML:', {
        mapName,
        legacyLayersCount: legacyLayers.length,
        legacyLayers: legacyLayers.map(l => ({ name: l.layerName, placeMarks: l.placeMarks.length })),
        fleetsCount: fleets.length,
        fixedRoutesCount: fixedRoutes ? (fixedRoutes.routes?.length || 0) : 0
      });
      
      // Convert legacy layers to current format
      const currentLayers = convertLegacyToCurrentLayers(legacyLayers);

      // Migration / de-dupe: fixed-route lines are reconstructed from the
      // "Fixed Routes Configuration" by useFixedRoutesSync after load. Maps saved
      // before that became the single source of truth also baked the route lines
      // into the layer as plain placemarks. Strip line shapes out of the
      // fixed-routes layers when we have a config to rebuild from, otherwise each
      // route would show twice in the panel (stale line + synced line).
      const FIXED_ROUTE_LAYER_NAMES = ['Fixed Routes', 'Optimised Fixed Route Network'];
      const hasFixedRoutesConfig = !!fixedRoutes && (fixedRoutes.routes?.length ?? 0) > 0;
      const dedupedLayers = hasFixedRoutesConfig
        ? currentLayers.map(layer =>
            FIXED_ROUTE_LAYER_NAMES.includes(layer.layerName)
              ? { ...layer, shapes: layer.shapes.filter(shape => shape.type !== 'line') }
              : layer
          )
        : currentLayers;

      console.log('📊 Loaded layers:', dedupedLayers.length, 'layers with',
        dedupedLayers.reduce((total, layer) => total + layer.shapes.length, 0), 'shapes');

      // Update context with loaded map
      setMapName(mapName);
      setLayers(dedupedLayers);
      setFleets(fleets);
      
      // Restore fixed routes if present
      try {
        if (fixedRoutes && (fixedRoutes.routes?.length > 0 || fixedRoutes.stops?.length > 0)) {
          console.log('🔄 Restoring fixed routes:', fixedRoutes.routes.length, 'routes,', fixedRoutes.stops.length, 'stops');
          // Log route colors for debugging
          fixedRoutes.routes.forEach(route => {
            console.log(`  Route "${route.name}" color:`, route.color || '(not set)');
          });
          // Clear existing fixed routes
          fixedRoutesContext.clearAllData();
          // Add stops first
          fixedRoutes.stops.forEach(stop => {
            fixedRoutesContext.addStop(stop);
          });
          // Then add routes (preserving all fields including color)
          fixedRoutes.routes.forEach(route => {
            fixedRoutesContext.addRoute(route);
          });
        }
      } catch (error) {
        console.error('Error restoring fixed routes:', error);
        // Don't fail map loading if fixed routes restoration fails
      }
      
      // Find center point for map - prioritize fixed route stops, then layer points
      let centerLat: number | undefined;
      let centerLng: number | undefined;
      
      // First, try to center on fixed route stops if available
      if (fixedRoutes && fixedRoutes.stops && fixedRoutes.stops.length > 0) {
        const firstStop = fixedRoutes.stops[0];
        if (firstStop.coordinates) {
          centerLat = firstStop.coordinates.lat;
          centerLng = firstStop.coordinates.lng;
          console.log('📍 Centering on fixed route stop:', centerLat, centerLng);
        }
      }
      
      // If no fixed route stops, try to center on layer points
      if (centerLat === undefined && legacyLayers.length > 0 && legacyLayers[0].placeMarks.length > 0) {
        const firstPoint = legacyLayers[0].placeMarks.find(pm => pm.type === 'point');
        if (firstPoint && firstPoint.latitude !== undefined && firstPoint.longitude !== undefined) {
          centerLat = firstPoint.latitude;
          centerLng = firstPoint.longitude;
          console.log('📍 Centering on layer point:', centerLat, centerLng);
        }
      }
      
      // Set map center if we found a valid point
      if (centerLat !== undefined && centerLng !== undefined) {
        setMapCentre({
          latitude: centerLat,
          longitude: centerLng,
          zoom: 12
        });
      } else {
        console.warn('⚠️ No valid center point found for map');
      }

      // After loading all objects into context, fit the Leaflet map to the loaded content.
      // This makes "open map" behave like imports (auto-zoom to relevant geometry).
      window.dispatchEvent(new CustomEvent('fitMapToObjects'));
      
      console.log('✅ Map loaded successfully:', mapName);
      
      // Track load success
      logEvent({
        category: 'Map',
        action: 'LoadCompleted',
        label: 'Map Load Success',
        mapId: map.zone_id,
        mapName: mapName,
        layerCount: currentLayers.length,
        shapeCount: currentLayers.reduce((total, layer) => total + layer.shapes.length, 0),
        duration: Date.now() - startTime,
        loadSource: 'gallery'
      });
      
      // Notify parent that a map was loaded so it can track the ID
      if (onMapLoaded) {
        onMapLoaded(map.zone_id);
      }
      
      onClose();
    } catch (err) {
      console.error('Error loading map:', err);
      const errorMessage = err instanceof Error ? err.message : t('mapGallery.errorLoadMap');
      setError(errorMessage);
      
      // Track load failure
      logEvent({
        category: 'Map',
        action: 'LoadError',
        label: 'Map Load Failed',
        mapId: map.zone_id,
        mapName: map.zone_name,
        error: errorMessage,
        duration: Date.now() - startTime,
        loadSource: 'gallery'
      });
    } finally {
      setLoadingMapId(null);
    }
  };

  const handleDeleteMap = async (mapId: string, mapName: string) => {
    if (!window.confirm(t('mapGallery.confirmDeleteMap', { name: mapName }))) {
      return;
    }

    try {
      await mapService.deleteMap(mapId);
      // Refresh the maps list
      await loadMaps();
    } catch (err) {
      console.error('Error deleting map:', err);
      const errorMessage = err instanceof Error ? err.message : t('mapGallery.errorDeleteMap');
      setError(errorMessage);
    }
  };

  const handleMapSelection = (map: SavedMap) => {
    if (!hasUnsavedChanges) {
      void handleLoadMap(map);
      return;
    }

    setPendingLoadMap(map);
    setDraftMapName((currentMapName || '').trim());
    setConfirmError(null);
    setConfirmUnsavedOpen(true);
  };

  const handleContinueWithoutSaving = () => {
    if (!pendingLoadMap) return;
    const selectedMap = pendingLoadMap;
    setConfirmUnsavedOpen(false);
    setPendingLoadMap(null);
    void handleLoadMap(selectedMap);
  };

  const handleSaveAndContinue = async () => {
    if (!pendingLoadMap) return;

    const trimmedName = draftMapName.trim();
    if (!trimmedName) {
      setConfirmError(t('mapGallery.errorSaveMapName'));
      return;
    }

    setSaveAndContinueLoading(true);
    setConfirmError(null);

    try {
      onMapNameChange?.(trimmedName);
      const didSave = onSaveMap ? await onSaveMap(trimmedName) : true;
      if (!didSave) {
        setConfirmError(t('mapGallery.errorSaveMapValidation'));
        return;
      }

      const selectedMap = pendingLoadMap;
      setConfirmUnsavedOpen(false);
      setPendingLoadMap(null);
      await handleLoadMap(selectedMap);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : t('mapGallery.errorSaveMap');
      setConfirmError(message);
    } finally {
      setSaveAndContinueLoading(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        {t('mapGallery.title')}
        <Typography variant="body2" color="text.secondary">
          {t('mapGallery.subtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 1 }}>
          <Tabs
            value={mapsFilterTab}
            onChange={(_, value) => setMapsFilterTab(value)}
            aria-label={t('mapGallery.filterTabsAriaLabel')}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="all" label={t('mapGallery.filterAll', { count: maps.length })} />
            <Tab value="owned" label={t('mapGallery.filterOwned', { count: ownedMaps.length })} />
            <Tab value="shared" label={t('mapGallery.filterShared', { count: sharedMaps.length })} />
          </Tabs>
        </Box>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={loadMaps} sx={{ ml: 2 }}>
              {t('common.retry')}
            </Button>
          </Alert>
        )}

        {!loading && filteredMaps.length === 0 && !error && (
          <Box display="flex" flexDirection="column" alignItems="center" p={4}>
            <Icon size={64} name="image" style={{ color: '#52565e', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary">
              {mapsFilterTab === 'owned'
                ? t('mapGallery.noOwnedMaps')
                : mapsFilterTab === 'shared'
                  ? t('mapGallery.noSharedMaps')
                  : t('mapGallery.noSavedMaps')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {mapsFilterTab === 'shared'
                ? t('mapGallery.sharedHint')
                : t('mapGallery.createHint')}
            </Typography>
          </Box>
        )}
        
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 2,
            mt: 2
          }}
        >
          {filteredMaps.map((map) => (
            <Card 
              key={map.zone_id}
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
                <CardMedia
                  component="div"
                  sx={{
                    height: 140,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onClick={() => handleMapSelection(map)}
                >
                  {/* Base layer: always render a stylish fallback background so the tile is never blank */}
                  <Box
                    sx={(theme) => ({
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: opsColors.neutral.inkSoft,
                      background: `radial-gradient(120% 140% at 20% 10%, ${theme.palette.surface?.elevated ?? opsColors.neutral.panel} 0%, ${theme.palette.surface?.secondary ?? opsColors.neutral.bg} 55%, ${theme.palette.surface?.primary ?? opsColors.neutral.bg} 100%)`,
                      border: `1px solid ${opsColors.neutral.line}`,
                    })}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        opacity: 0.18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transform: 'rotate(-12deg) scale(1.25)',
                      }}
                    >
                      <Icon size={130} name="rocket" />
                    </Box>
                    {/* Foreground hint for missing/broken thumbnails */}
                    {(!map.thumbnail_url || brokenThumbnails.has(map.zone_id)) && (
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Icon size={24} name="rocket" />
                        <Typography variant="caption" color="text.secondary">
                          {t('mapGallery.noThumbnail')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Image layer */}
                  {map.thumbnail_url && !brokenThumbnails.has(map.zone_id) && (
                    <Box
                      component="img"
                      src={map.thumbnail_url}
                      alt={t('mapGallery.thumbnailAlt', { name: map.zone_name })}
                      loading="lazy"
                      onError={() => {
                        setBrokenThumbnails((prev) => {
                          const next = new Set(prev);
                          next.add(map.zone_id);
                          return next;
                        });
                      }}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                  
                  {loadingMapId === map.zone_id && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                </CardMedia>
                
                <CardContent sx={{ pb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, mr: 1 }}>
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        {map.zone_name}
                      </Typography>
                      {map.role && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={map.role === 'Owner' ? t('mapGallery.ownerChip') : t('mapGallery.sharedChip', { role: map.role })}
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={t('mapGallery.manageAccess')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareMap(map);
                          }}
                          sx={{ color: 'text.secondary' }}
                          aria-label={t('mapGallery.manageAccessAria', { name: map.zone_name })}
                        >
                          <Icon d={ICON_FALLBACKS.Share} size={20} />
                        </IconButton>
                      </Tooltip>
                      {map.role === 'Owner' ? (
                        <Tooltip title={t('mapGallery.deleteMap')}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteMap(map.zone_id, map.zone_name);
                            }}
                            sx={{ color: 'text.secondary' }}
                            aria-label={t('mapGallery.deleteMapAria', { name: map.zone_name })}
                          >
                            <Icon name="trash" size={20} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={map.role ? t('mapGallery.deleteMapAccess', { role: map.role }) : t('mapGallery.deleteMapNotOwned')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled
                              sx={{ color: 'text.secondary' }}
                              aria-label={t('mapGallery.deleteMapAria', { name: map.zone_name })}
                            >
                              <Icon name="trash" size={20} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Map Details */}
                  {mapDetails.has(map.zone_id) ? (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                        {(() => {
                          const details = mapDetails.get(map.zone_id)!;
                          return (
                            <>
                              {details.layers > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Icon size={16} d={ICON_FALLBACKS.Layers} style={{ color: '#52565e' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {t('mapGallery.layersCount', { count: details.layers })}
                                  </Typography>
                                </Box>
                              )}
                              {details.shapes > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('mapGallery.shapesCount', { count: details.shapes })}
                                  </Typography>
                                </Box>
                              )}
                              {details.fixedRoutes > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Icon size={16} name="route" style={{ color: '#ef7335' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {t('mapGallery.routesCount', { count: details.fixedRoutes })}
                                  </Typography>
                                </Box>
                              )}
                              {details.stops > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('mapGallery.stopsCount', { count: details.stops })}
                                  </Typography>
                                </Box>
                              )}
                              {details.fleets > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Icon size={16} name="bus" style={{ color: '#2e7d32' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {t('mapGallery.fleetsCount', { count: details.fleets })}
                                  </Typography>
                                </Box>
                              )}
                              {details.layers === 0 && details.shapes === 0 && details.fixedRoutes === 0 && details.stops === 0 && details.fleets === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  {t('mapGallery.emptyMap')}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {t('mapGallery.loadingDetails')}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
          ))}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>

      <ShareAccessDialog
        open={Boolean(shareMap)}
        title={shareMap ? t('mapGallery.shareTitle', { name: shareMap.zone_name }) : t('mapGallery.shareTitleFallback')}
        currentUserEmail={currentUserEmail}
        canManage={shareMap?.role === 'Owner'}
        listShares={() => mapService.listMapShares(shareMap!.zone_id)}
        share={(email, role) => mapService.shareMap(shareMap!.zone_id, { email, role })}
        revoke={(email) => mapService.revokeMapShare(shareMap!.zone_id, email)}
        onClose={() => setShareMap(null)}
      />
    </Dialog>

    <Dialog
      open={confirmUnsavedOpen}
      onClose={() => {
        if (saveAndContinueLoading) return;
        setConfirmUnsavedOpen(false);
        setPendingLoadMap(null);
        setConfirmError(null);
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          borderRadius: 3,
          background: `linear-gradient(180deg, ${opsColors.neutral.panel} 0%, ${opsColors.neutral.panel} 100%)`,
          border: `1px solid ${opsColors.neutral.line}`,
          boxShadow: theme.shadows[8],
        }),
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <Icon size={20} name="triangle-exclamation" style={{ color: '#ef7335' }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('mapGallery.unsavedHeader')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('mapGallery.unsavedBody', { name: pendingLoadMap?.zone_name ?? '' })}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          label={t('mapGallery.mapNameLabel')}
          value={draftMapName}
          inputRef={unsavedNameInputRef}
          onChange={(event) => {
            setDraftMapName(event.target.value);
            if (confirmError) setConfirmError(null);
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
          placeholder={t('mapGallery.mapNamePlaceholder')}
          margin="dense"
        />
        {confirmError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {confirmError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={() => {
            setConfirmUnsavedOpen(false);
            setPendingLoadMap(null);
            setConfirmError(null);
          }}
          disabled={saveAndContinueLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleContinueWithoutSaving}
          color="inherit"
          disabled={saveAndContinueLoading}
        >
          {t('mapGallery.continueWithoutSaving')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveAndContinue}
          disabled={saveAndContinueLoading}
          sx={(theme) => ({
            backgroundColor: opsColors.brand.primary,
            '&:hover': { backgroundColor: opsColors.brand.primaryHover },
          })}
        >
          {saveAndContinueLoading ? t('mapGallery.saving') : t('mapGallery.saveAndContinue')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}